import { Navigate } from 'react-router';
import { useAuthStore } from '@/stores/auth-store';
import { useAppVariant } from '@/lib/app-variant';

/**
 * Route guard for OptiEdge MDM pages (/app/mdm/*).
 *
 * - On the `optiedge` variant: MDM is always available (variant itself gates).
 * - On the `optisigns` variant: requires `company.feature.mdm === true`.
 *   Otherwise redirects to /app/screenManagement.
 *
 * Waits for auth/company to load (matches `RestrictedRoute.tsx` / `AdminRoute.tsx`)
 * so we don't flash a redirect during boot.
 *
 * Spec: docs/specs/19-optiedge-mdm/01-optiedge-mdm-phase-1.md §5.5
 */
export function MdmFeatureRoute({ children }: { children: React.ReactNode }) {
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);
  const variant = useAppVariant();

  // While user data is loading, don't flash redirect
  if (!me) return null;

  // OptiEdge variant always allows MDM pages.
  if (variant === 'optiedge') {
    return <>{children}</>;
  }

  // feature.mdm is a new OptiEdge-Phase-1 company flag — not yet in the
  // CompanyFeatures TypeScript type (packages/shared-types finalized in
  // Phase 3). Read it via a narrow structural check.
  const mdmEnabled = (company?.feature as Record<string, unknown> | undefined)?.mdm === true;
  if (!mdmEnabled) {
    return <Navigate to="/app/screenManagement" replace />;
  }

  return <>{children}</>;
}
